import { useEffect, useMemo, useState } from 'react'
import type { BootstrapDto, MetricsDto } from '../types'

const BREAK_STATE_TO_SETTING = {
  BREAKFAST: 'excludeBreakfast',
  LUNCH: 'excludeLunch',
  DINNER: 'excludeDinner',
  DISTRACTED: 'excludeDistraction',
  SMOKING: 'excludeSmoking',
} as const

export function useLiveMetrics(data?: BootstrapDto): MetricsDto | undefined {
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  return useMemo(() => {
    if (!data) return undefined
    return calculateLiveMetrics(data, now)
  }, [data, now])
}

export function calculateLiveMetrics(data: BootstrapDto, now: number): MetricsDto {
  const source = data.cycleMetrics
  const hasTarget = source.targetSeconds > 0
  const original = hasTarget ? source : {
    ...source,
    remainingSeconds: 0,
    overtimeSeconds: 0,
    progress: 0,
    projectedExitAt: undefined,
  }
  if (!data.activeSession) return original

  const elapsed = Math.max(0, Math.floor((now - new Date(data.serverTime).getTime()) / 1000))
  const state = data.user.currentState
  const breakSetting = BREAK_STATE_TO_SETTING[state as keyof typeof BREAK_STATE_TO_SETTING]
  const isExcludedBreak = breakSetting ? data.settings[breakSetting] : false
  const gross = original.grossSeconds + elapsed
  const excluded = original.excludedBreakSeconds + (isExcludedBreak ? elapsed : 0)
  const net = original.netSeconds + (isExcludedBreak ? 0 : elapsed)
  const distraction = (original.distractionSeconds || 0) + (state === 'DISTRACTED' ? elapsed : 0)
  const remaining = hasTarget ? Math.max(0, original.targetSeconds - net) : 0
  const overtime = hasTarget ? Math.max(0, net - original.targetSeconds) : 0
  return {
    ...original,
    grossSeconds: gross,
    excludedBreakSeconds: excluded,
    distractionSeconds: distraction,
    netSeconds: net,
    remainingSeconds: remaining,
    overtimeSeconds: overtime,
    progress: hasTarget ? Math.min(1, net / original.targetSeconds) : 0,
    projectedExitAt: hasTarget ? new Date(now + remaining * 1000).toISOString() : undefined,
  }
}
